import { Check } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";

import { usePaymentCheckout } from "../hooks/usePaymentCheckout";
import {
  trackConversion,
  trackFormAnalytics,
  trackFunnelStep,
  trackPageView,
  trackPerformance,
  trackUserJourney,
} from "../utils/analytics";
import { publicAnonKey, supabaseUrl } from "../utils/supabase/info";

import EmailVerificationScreen from "./EmailVerificationScreen";
import LandingPage from "./LandingPage";
import LegalPage from "./LegalPage";
import { PaymentSuccessScreen, PaymentStatus } from "./PaymentSuccessScreen";
import { Button } from "./ui/button";
import WaitlistScreen, { WaitlistData } from "./WaitlistScreen";

export type Screen =
  | "landing"
  | "legal"
  | "waitlist"
  | "emailVerification"
  | "waitlistComplete"
  | "paymentSuccess"
  | "paymentCancelled";

export interface User {
  id: string;
  email: string;
  name: string;
  phoneNumber?: string;
  isVerified: boolean;
}

export interface AppState {
  currentScreen: Screen;
  user: User | null;
  isLoading: boolean;
  verificationEmail?: string;
  submissionId?: string;
  isDemoMode: boolean;
  verificationToken?: string;
  healthError?: string;
  paymentTier: "free" | "priority" | "founding";
  paymentSessionId?: string;
  paymentStatus?: PaymentStatus;
}

const initialState: AppState = {
  currentScreen: "landing",
  user: null,
  isLoading: false,
  isDemoMode: false,
  paymentTier: "free",
};

// Hash routing utility
const getScreenFromHash = (hash: string): Screen => {
  const cleanHash = hash.replace("#", "");
  if (cleanHash === "legal") return "legal";
  if (cleanHash === "waitlist") return "waitlist";
  if (cleanHash === "email-verification") return "emailVerification";
  if (cleanHash === "complete") return "waitlistComplete";
  if (cleanHash === "payment-success") return "paymentSuccess";
  if (cleanHash === "payment-cancelled") return "paymentCancelled";
  return "landing";
};

const getHashFromScreen = (screen: Screen): string => {
  switch (screen) {
    case "legal":
      return "#legal";
    case "waitlist":
      return "#waitlist";
    case "emailVerification":
      return "#email-verification";
    case "waitlistComplete":
      return "#complete";
    case "paymentSuccess":
      return "#payment-success";
    case "paymentCancelled":
      return "#payment-cancelled";
    default:
      return "";
  }
};

// Check for payment URL parameters (from Stripe redirect)
const checkPaymentParams = (): {
  sessionId?: string;
  tier?: "priority" | "founding";
  cancelled?: boolean;
} => {
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get("session_id");
  const tierParam = urlParams.get("tier");
  const cancelled = urlParams.get("cancelled") === "true";

  // Only accept valid tier values - use constant array for maintainability
  const validPaidTiers = ["priority", "founding"] as const;
  type PaidTier = (typeof validPaidTiers)[number];
  const isValidTier = (tier: string | null): tier is PaidTier =>
    tier !== null && validPaidTiers.includes(tier as PaidTier);

  if (cancelled) {
    // Only include tier if valid to comply with exactOptionalPropertyTypes
    if (isValidTier(tierParam)) {
      return { cancelled: true, tier: tierParam };
    }
    return { cancelled: true };
  }

  if (sessionId && isValidTier(tierParam)) {
    return {
      sessionId,
      tier: tierParam,
    };
  }

  return {};
};

// Check for verification URL parameters
const checkVerificationParams = (): { token?: string; email?: string } => {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");
  const email = urlParams.get("email");

  if (token && email) {
    return { token, email };
  }

  return {};
};

export default function Router() {
  const [appState, setAppState] = useState<AppState>(initialState);
  const [hasError, setHasError] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setEmailServiceStatus] = useState<{
    working: boolean;
    message?: string;
  }>({ working: true });

  // Payment checkout hook - used for paid tier redirects to Stripe Checkout
  const {
    initiateCheckout,
    error: checkoutError,
    clearError: clearCheckoutError,
  } = usePaymentCheckout();

  // Initialize routing and analytics
  useEffect(() => {
    // Check for payment parameters first (from Stripe redirect)
    const paymentParams = checkPaymentParams();
    if (paymentParams.cancelled) {
      // User cancelled payment at Stripe
      const cancelledTier = paymentParams.tier ?? "free";
      setAppState(prev => ({
        ...prev,
        currentScreen: "paymentCancelled",
        paymentTier: cancelledTier,
        paymentStatus: "cancelled",
      }));
      // Clean up URL params
      window.history.replaceState(
        {},
        "",
        window.location.pathname + "#payment-cancelled"
      );
      return;
    }

    if (paymentParams.sessionId && paymentParams.tier) {
      // Successful payment return from Stripe - both sessionId and tier are definitely defined here
      const successTier = paymentParams.tier;
      const successSessionId = paymentParams.sessionId;
      setAppState(prev => ({
        ...prev,
        currentScreen: "paymentSuccess",
        paymentTier: successTier,
        paymentSessionId: successSessionId,
        paymentStatus: "success",
      }));
      // Clean up URL params
      window.history.replaceState(
        {},
        "",
        window.location.pathname + "#payment-success"
      );
      return;
    }

    // Check for verification parameters
    const verificationParams = checkVerificationParams();
    if (verificationParams.token && verificationParams.email) {
      // Handle verification flow
      handleEmailVerification(
        verificationParams.token,
        verificationParams.email
      );
      return;
    }

    // Set initial screen from hash
    const initialScreen = getScreenFromHash(window.location.hash);
    if (initialScreen !== "landing") {
      setAppState(prev => ({ ...prev, currentScreen: initialScreen }));
    }

    // Track initial page view
    trackPageView(
      window.location.pathname + window.location.hash,
      document.title
    );
    // Track initial page view for PMF metrics (user_journey event)
    trackUserJourney("page_view", { page: initialScreen });

    // Listen for hash changes
    const handleHashChange = () => {
      const newScreen = getScreenFromHash(window.location.hash);
      if (newScreen !== appState.currentScreen) {
        navigateToScreen(newScreen);
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle email verification from URL parameters
  const handleEmailVerification = async (token: string, email: string) => {
    try {
      setLoading(true);

      // Track verification attempt
      trackConversion("email_verified", "email_link", undefined, {
        user_type: "waitlist",
      });

      const response = await fetch(
        `${supabaseUrl}/functions/v1/verify-email?token=${token}&email=${encodeURIComponent(email)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${publicAnonKey}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to verify email");
      }

      const data = await response.json();
      if (data.error) {
        setHasError(true);
        return;
      }

      if (data.success) {
        setAppState(prev => ({
          ...prev,
          verificationToken: token,
          currentScreen: "emailVerification",
        }));
        trackConversion("email_verified", "waitlist", undefined, {
          user_type: "waitlist",
        });
        trackConversion("email_verified", "email_link_click", undefined, {
          user_type: "waitlist",
        });
        setTimeout(() => {
          handleEmailVerified();
        }, 5000);
      }
      // Set verification data and navigate to verification screen
      setVerificationEmail(email);
      setSubmissionId(token);
      setAppState(prev => ({
        ...prev,
        verificationToken: token,
        currentScreen: "emailVerification",
      }));

      // Update hash to reflect verification state
      window.location.hash = "#email-verification";

      // Track successful verification flow start
      trackConversion("email_verified", "waitlist");
      trackFunnelStep("email_verification", {
        email_domain: email.split("@")[1],
      });
    } catch (error) {
      console.error("Error during email verification:", error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const navigateToScreen = (screen: Screen) => {
    try {
      // Update hash for better analytics
      const newHash = getHashFromScreen(screen);
      if (window.location.hash !== newHash) {
        window.location.hash = newHash;
      }

      setAppState(prev => ({
        ...prev,
        currentScreen: screen,
      }));

      // Track navigation with enhanced analytics
      trackPageView(
        `/${screen}`,
        `Koru - ${screen.charAt(0).toUpperCase() + screen.slice(1)}`
      );
      trackUserJourney("page_view", { page: screen });

      // Track specific funnel steps
      switch (screen) {
        case "waitlist":
          trackFunnelStep("waitlist_start");
          break;
        case "emailVerification":
          trackFunnelStep("email_verification");
          break;
        case "waitlistComplete":
          trackFunnelStep("verification_complete");
          break;
      }
    } catch (error) {
      console.error("Error navigating to screen:", error);
      setHasError(true);
    }
  };

  const startWaitlist = async () => {
    try {
      setLoading(true);

      // First check health endpoint to see if waitlist is available
      const healthResponse = await fetch(`${supabaseUrl}/functions/v1/health`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${publicAnonKey}`,
        },
      });

      if (!healthResponse.ok) {
        // If health check fails, show error
        setHasError(true);
        setAppState(prev => ({
          ...prev,
          healthError:
            "This form is currently unavailable, please try again later",
        }));
        return;
      }

      const healthData = await healthResponse.json();

      if (!healthData.waitlistAvailable) {
        // If waitlist is closed, show different error
        setHasError(true);
        setAppState(prev => ({
          ...prev,
          healthError:
            "The waitlist has been closed and we are no longer accepting submissions",
        }));
        return;
      }

      // If everything is good, proceed with waitlist
      trackConversion("demo_start", "landing_page");
      trackFunnelStep("waitlist_start", { source: "landing_page" });

      navigateToScreen("waitlist");
    } catch (error) {
      console.error("Error starting waitlist:", error);
      setHasError(true);
      setAppState(prev => ({
        ...prev,
        healthError:
          "This form is currently unavailable, please try again later",
      }));
    } finally {
      setLoading(false);
    }
  };

  const setLoading = (isLoading: boolean) => {
    try {
      setAppState(prev => ({
        ...prev,
        isLoading,
      }));
    } catch (error) {
      console.error("Error setting loading state:", error);
      setHasError(true);
    }
  };

  const setVerificationEmail = (email: string) => {
    try {
      setAppState(prev => ({
        ...prev,
        verificationEmail: email,
      }));
    } catch (error) {
      console.error("Error setting verification email:", error);
      setHasError(true);
    }
  };

  const setSubmissionId = (submissionId: string) => {
    try {
      setAppState(prev => ({
        ...prev,
        submissionId,
      }));
    } catch (error) {
      console.error("Error setting submission ID:", error);
      setHasError(true);
    }
  };

  const handleWaitlistSubmit = async (
    waitlistData: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    step: number = 1,
    isFinalStep: boolean = false
  ) => {
    try {
      setLoading(true);

      // Track form submission with detailed analytics
      const startTime = performance.now();
      trackFormAnalytics("submit_attempt", "waitlist_form");
      trackFunnelStep("form_submit", { source: "waitlist_form" });

      // Add step and final step info to the request
      const requestData = {
        ...waitlistData,
        step,
        is_final_step: isFinalStep,
      };

      // Send to Supabase
      const response = await fetch(`${supabaseUrl}/functions/v1/waitlist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error(
            data.message ||
              "This form has already been completed and cannot be edited."
          );
        }
        if (response.status >= 500) {
          throw new Error(
            data.message ||
              "An unexpected error has occurred, please try again later"
          );
        }
        throw new Error(data.message || "Failed to submit to waitlist");
      }

      // Track successful submission
      const submissionTime = performance.now() - startTime;
      trackFormAnalytics("submit_success", "waitlist_form", undefined);
      trackPerformance("form_submission_time", submissionTime, {
        element: "waitlist_form",
      });

      // Track step completion
      if (isFinalStep) {
        trackFunnelStep("verification_complete");

        // Determine flow based on selected tier
        const tier = (waitlistData as WaitlistData).selectedTier;

        if (tier && tier !== "free") {
          // Paid tier → redirect to Stripe Checkout
          // The hook handles the redirect, so we don't navigate here
          await initiateCheckout(waitlistData.email, tier);
          // If we're still here, the redirect failed - error is in checkoutError state
          return;
        }

        // Free tier (or undefined) → continue to email verification
        setVerificationEmail(waitlistData.email);
        setSubmissionId(data.submissionId);
        navigateToScreen("emailVerification");
      } else {
        trackFunnelStep("form_submit");
        // Continue to next step - update app state with response data
        setAppState(prev => ({
          ...prev,
          user: data.user || prev.user,
        }));
      }

      // Track email service status only on final step
      if (isFinalStep) {
        setEmailServiceStatus({
          working: data.emailSent === true,
          message:
            data.emailSent === false
              ? data.emailError || "Email service unavailable"
              : undefined,
        });
      }
    } catch (error) {
      console.error("Waitlist submission error:", error);
      trackFormAnalytics("validation_error", "waitlist_form", undefined);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleEmailVerified = () => {
    try {
      trackConversion("email_verified", "waitlist");
      trackFunnelStep("verification_complete", {
        email_domain: appState.verificationEmail?.split("@")[1] ?? "unknown",
      });

      navigateToScreen("waitlistComplete");
    } catch (error) {
      console.error("Error handling email verification:", error);
      setHasError(true);
    }
  };

  const handleVerifyLater = () => {
    try {
      trackUserJourney("drop_off", {
        page: "email_verification",
        feature: "email_verification",
        time_spent: 0, // You could track actual time spent here
      });

      navigateToScreen("waitlistComplete");
    } catch (error) {
      console.error("Error handling verify later:", error);
      setHasError(true);
    }
  };

  // Error fallback UI (includes checkout errors)
  if (hasError || checkoutError) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center p-8 max-w-md mx-auto">
          <h2 className="text-xl font-semibold mb-4">
            {checkoutError
              ? "Payment Error"
              : appState.healthError
                ? "Waitlist Unavailable"
                : "Something went wrong"}
          </h2>
          <p className="text-muted-foreground mb-4">
            {checkoutError ??
              appState.healthError ??
              "Please refresh the page to try again."}
          </p>
          <button
            onClick={() => {
              if (checkoutError) {
                // If it's a checkout error, clear it and go back to waitlist
                clearCheckoutError();
                navigateToScreen("waitlist");
              } else if (appState.healthError) {
                // If it's a health error, reset to landing
                setHasError(false);
                setAppState(prev => {
                  const newState = { ...prev };
                  delete newState.healthError;
                  return newState;
                });
                navigateToScreen("landing");
              } else {
                // Otherwise refresh the page
                window.location.reload();
              }
            }}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            {checkoutError
              ? "Try Again"
              : appState.healthError
                ? "Back to Home"
                : "Refresh Page"}
          </button>
        </div>
      </div>
    );
  }

  const pageVariants = {
    initial: { opacity: 0, x: 20 },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: -20 },
  };

  const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.4,
  };

  const renderCurrentScreen = () => {
    try {
      const screenProps = {
        initial: "initial" as const,
        animate: "in" as const,
        exit: "out" as const,
        variants: pageVariants,
        transition: pageTransition,
      };

      switch (appState.currentScreen) {
        case "landing":
          return (
            <motion.div key="landing" {...screenProps}>
              <LandingPage
                onTryApp={startWaitlist}
                isLoading={appState.isLoading}
              />
            </motion.div>
          );

        case "legal":
          return (
            <motion.div key="legal" {...screenProps}>
              <LegalPage onBack={() => navigateToScreen("landing")} />
            </motion.div>
          );

        case "waitlist":
          return (
            <motion.div key="waitlist" {...screenProps}>
              <WaitlistScreen
                onSubmit={handleWaitlistSubmit}
                onBack={() => navigateToScreen("landing")}
                isLoading={appState.isLoading}
              />
            </motion.div>
          );

        case "emailVerification":
          if (!appState.verificationEmail || !appState.submissionId) {
            navigateToScreen("landing");
            return null;
          }
          return (
            <motion.div key="emailVerification" {...screenProps}>
              <EmailVerificationScreen
                email={appState.verificationEmail}
                submissionId={appState.submissionId}
                onVerified={handleEmailVerified}
                onVerifyLater={handleVerifyLater}
                onBack={() => navigateToScreen("waitlist")}
                isPreVerified={!!appState.verificationToken}
              />
            </motion.div>
          );

        case "waitlistComplete":
          return (
            <motion.div key="waitlistComplete" {...screenProps}>
              <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center p-4">
                <div className="text-center space-y-6 max-w-md">
                  <div className="w-20 h-20 mx-auto bg-primary/20 rounded-full flex items-center justify-center">
                    <Check className="w-10 h-10 text-primary" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold mb-2">
                      You&apos;re on the waitlist!
                    </h1>
                    <p className="text-muted-foreground">
                      Thanks for joining. We&apos;ll keep you updated on our
                      progress and notify you when beta access is available.
                    </p>
                  </div>
                  <Button
                    onClick={() => navigateToScreen("landing")}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    Back to Home
                  </Button>
                </div>
              </div>
            </motion.div>
          );

        case "paymentSuccess":
          return (
            <motion.div key="paymentSuccess" {...screenProps}>
              <PaymentSuccessScreen
                status="success"
                tier={appState.paymentTier}
                {...(appState.paymentSessionId && {
                  sessionId: appState.paymentSessionId,
                })}
                onGoHome={() => navigateToScreen("landing")}
              />
            </motion.div>
          );

        case "paymentCancelled":
          return (
            <motion.div key="paymentCancelled" {...screenProps}>
              <PaymentSuccessScreen
                status="cancelled"
                tier={appState.paymentTier}
                onRetry={() => navigateToScreen("waitlist")}
                onGoHome={() => navigateToScreen("landing")}
              />
            </motion.div>
          );

        default:
          return (
            <motion.div key="fallback" {...screenProps}>
              <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
                <p>Loading...</p>
              </div>
            </motion.div>
          );
      }
    } catch (error) {
      console.error("Error rendering screen:", error);
      setHasError(true);
      return null;
    }
  };

  // For landing page, use full width. For app, use mobile container
  const containerClass =
    "min-h-screen bg-background text-foreground dark overflow-x-hidden";

  const contentClass =
    appState.currentScreen === "landing" || appState.currentScreen === "legal"
      ? "min-h-screen relative w-full overflow-x-hidden"
      : "max-w-md mx-auto min-h-screen relative overflow-hidden w-full px-4 box-border";

  return (
    <div className={containerClass}>
      <div className={contentClass}>
        <AnimatePresence mode="wait">{renderCurrentScreen()}</AnimatePresence>
      </div>
    </div>
  );
}
